import React, { useContext, useState } from 'react';
import { useHistory, useParams } from 'react-router-dom';
import {
  createStyles,
  makeStyles,
  Divider,
  Theme,
  Typography,
  Button,
  Box,
  Grid,
  CircularProgress,
} from '@material-ui/core';
import PaperColumn from '../components/PaperColumn';
import ApiContext from '../contexts/api';
import { CardPaymentsApi } from '../clients/CardPaymentsApi';
import { UriParams } from '../models/UriParams';
import { PageError } from '../extensions/PageError';
import { useMount } from '../hooks/useMount';
import LoadingSpinner from '../components/LoadingSpinner';
import { ErrorMessage } from '../components/ErrorMessage';
import { InvoiceDTO } from '../models/InvoiceDTO';
import { BillingApi } from '../clients/BillingApi';
import { CardTransaction } from '../models/CardTransaction';
import { getAppInsights } from '../TelemetryService';
import { GridItemProps } from '../models/GridItemProps';
import { withLineBreaks } from '../utils/utils';
import { withTransaction } from '@elastic/apm-rum-react';
import { Alert } from '@material-ui/lab';
import { PaymentMessages } from '../models/PaymentMessages';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(2),
    },
    divider: {
      margin: theme.spacing(3, 0),
    },
    centreText: {
      textAlign: 'center',
      marginTop: theme.spacing(2),
    },
    button: {
      color: '#FFF',
      minWidth: '150px',
      margin: '0 auto',
      display: 'flex',
      marginTop: theme.spacing(2),
      [theme.breakpoints.down('xs')]: {
        width: '100%',
      },
    },
    [theme.breakpoints.down('xs')]: {
      box: {
        flexDirection: 'column',
        '& > *': {
          justifyContent: 'center',
        },
      },
      textCentre: {
        textAlign: 'center',
      },
      info: {
        marginTop: theme.spacing(3),
        justifyContent: 'center',
      },
      spinner: {
        position: 'relative !important',
      },
    },
    spinner: {
      position: 'absolute',
      left: 0,
      right: 0,
      textAlign: 'center',
    },
    alert: {
      justifyContent: 'center',
      marginTop: theme.spacing(2),
    },
  })
);

const GridTransactionInfo: React.FunctionComponent<GridItemProps> = ({ label, value }) => {
  return (
    <>
      <Grid item xs={6} sm={4} md={2} lg={2}>
        {label} :
      </Grid>
      <Grid item xs={5} sm={5} md={9} lg={9}>
        {value}
      </Grid>
    </>
  );
};

type Loading = { type: 'Loading' };
type Error = { type: 'Error'; pageError: PageError };
type Loaded = {
  type: 'Loaded';
  cardTransaction: CardTransaction;
  invoice: InvoiceDTO;
};

type InvoiceState = Loading | Error | Loaded;

const PaymentSuccess: React.FunctionComponent = () => {
  const classes = useStyles();
  const { id, authNumber, cardTransactionId } = useParams<UriParams>();
  const { publicApiClient } = useContext(ApiContext)!;
  const cardPaymentsApi = new CardPaymentsApi(publicApiClient);
  const billingApi = new BillingApi(publicApiClient);
  const [state, setState] = useState<InvoiceState>({ type: 'Loading' });
  const history = useHistory();

  useMount(() => {
    getAppInsights().trackEvent({
      name: 'Successful Payment for InvoiceId: ' + id,
      properties: { invoiceId: id, transactionId: cardTransactionId },
    });

    (async () => {
      try {
        setState({
          type: 'Loaded',
          cardTransaction: await cardPaymentsApi.GetCardTransaction(cardTransactionId),
          invoice: await billingApi.GetInvoice(id, authNumber),
        });

        setTimeout(() => {
          history.push(`/invoice/${id}/${authNumber}`);
        }, 3000);
      } catch (e) {
        setState({ type: 'Error', pageError: e as PageError });
      }
    })();
  });

  const handleViewInvoice = async () => {
    history.push(`/invoice/${id}/${authNumber}`);
  };

  switch (state.type) {
    case 'Loading':
      return <LoadingSpinner />;
    case 'Error':
      return <ErrorMessage data={state.pageError} />;
    case 'Loaded':
      const { to, isGuest, isPaymentLinkedToASuspendedService } = state.invoice;
      const { amountIncVat, maskedCardNumber, brand, expiryDate } = state.cardTransaction;

      return (
        <div className={classes.root}>
          <PaperColumn>
            <Typography variant="h5" gutterBottom className={classes.centreText}>
              Payment Success
              <div>
                <Typography variant="subtitle2" className={classes.header}>
                  Please wait while we redirect you to the invoice
                </Typography>
              </div>
              <Divider variant="middle" className={classes.divider} />
              <div className={classes.spinner}>
                <CircularProgress />
              </div>
            </Typography>

            {!isGuest && (
              <Box p={1} className={classes.textCentre}>
                {withLineBreaks(to)}
              </Box>
            )}

            {isPaymentLinkedToASuspendedService && (
              <Typography variant="h5" gutterBottom className={classes.header}>
                <Alert severity="info" className={classes.alert}>
                  {PaymentMessages.SERVICE_LIFT_MESSAGE}
                </Alert>
              </Typography>
            )}

            <Box p={1} display="flex" className={classes.box}>
              <Grid container>
                <GridTransactionInfo
                  label="Amount inc vat"
                  value={'£ ' + amountIncVat.toFixed(2)}
                />
                <GridTransactionInfo label="Card no." value={maskedCardNumber} />
                <GridTransactionInfo label="Brand" value={brand} />
                <GridTransactionInfo label="Expiry date" value={expiryDate} />
              </Grid>
            </Box>

            <Typography variant="body1" gutterBottom className={classes.centreText}>
              If you are not redirected please click on the button below to view the full invoice
            </Typography>

            <Button
              size="medium"
              variant="contained"
              color="secondary"
              type="submit"
              className={classes.button}
              onClick={handleViewInvoice}
            >
              View Invoice
            </Button>
          </PaperColumn>
        </div>
      );
  }
};

export default withTransaction('PaymentSuccess', 'component')(PaymentSuccess);
